/**
 * Relative + absolute time helpers (Batch 30j-6).
 *
 * Pure functions — no `setInterval`, no React, no third-party deps. Tier
 * table per ``frontend/docs/30j_6_design.md`` §6.2 + Architect §7.
 *
 * BACKLOG `P3-foundations-relative-time-helper`: promote to
 * `frontend/src/lib/foundations/` once a second consumer surfaces. v1
 * locale is `en-US` only (BACKLOG `P3-foundations-relative-time-locale`).
 */

const PLURAL_RULES = new Intl.PluralRules("en-US");
const ABS_FORMAT = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
});
const DATE_FORMAT = new Intl.DateTimeFormat("en-US", { dateStyle: "medium" });

/**
 * Human-readable relative time. Tier table:
 *
 *   < 60s    → "just now"
 *   < 60m    → "<n> minute(s) ago"
 *   < 24h    → "<n> hour(s) ago"
 *   < 7d     → "<n> day(s) ago"
 *   < ~30d   → "<n> week(s) ago"   (5 weeks → still "weeks", floor by 30d)
 *   < 12mo   → "<n> month(s) ago"
 *   ≥ 1yr    → "<n> year(s) ago"
 *
 * @param iso  ISO 8601 timestamp (e.g. NoteOut.created_at).
 * @param now  Reference time. Defaults to `new Date()`. Tests inject a
 *             deterministic `now` to lock truth-table outputs.
 */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const ts = then.getTime();
  if (Number.isNaN(ts)) return "";
  const deltaMs = now.getTime() - ts;
  if (deltaMs < 0) return "just now"; // future-dated row (clock skew defense)

  const seconds = Math.floor(deltaMs / 1000);
  if (seconds < 60) return "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return tier(minutes, "minute");

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return tier(hours, "hour");

  const days = Math.floor(hours / 24);
  if (days < 7) return tier(days, "day");

  const weeks = Math.floor(days / 7);
  if (days < 30) return tier(weeks, "week");

  const months = Math.floor(days / 30);
  if (months < 12) return tier(months, "month");

  const years = Math.floor(days / 365);
  return tier(years, "year");
}

function tier(n: number, unit: string): string {
  const plural = PLURAL_RULES.select(n) === "one" ? unit : `${unit}s`;
  return `${n} ${plural} ago`;
}

/**
 * Absolute timestamp formatted via `Intl.DateTimeFormat` (en-US,
 * `dateStyle: "medium"` + `timeStyle: "short"`). Used for the `title`
 * attribute on `<time>` elements (browser-native tooltip on hover).
 */
export function absoluteTime(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  return ABS_FORMAT.format(then);
}

/**
 * Human date — NEVER a raw ISO / `YYYY-MM-DD` string in the UI (HoD teardown
 * P1-2). Renders e.g. "Jan 5, 2026". Bare date-only strings (`YYYY-MM-DD`,
 * the shape backend contract dates use) are parsed as LOCAL midnight so the
 * displayed day never drifts by a timezone offset. `null` / unparseable → "—".
 */
export function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  // Date-only string → parse components locally (avoid UTC-midnight TZ shift).
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return DATE_FORMAT.format(d);
}

/**
 * Relative time alias for Operator-surface call sites — "—" for empty input
 * (timestamps that may be absent), otherwise delegates to `relativeTime`.
 */
export function fmtRelative(value: string | null | undefined): string {
  if (!value) return "—";
  return relativeTime(value) || "—";
}
